// Analytics abstraction. Loads Meta Pixel / GA4 / GTM ONLY when IDs are present.
// Fires a consistent set of named events across providers + best-effort backend log.

import axios from "axios";
import { logger } from "@/lib/logger";

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_ID || "";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standard Meta events; everything else uses trackCustom.
// Ref: https://www.facebook.com/business/help/402791146561655
// Note: "InitiateCheckout" was intentionally dropped (product decision) so the
// only standard Meta conversion is CompleteRegistration on a successful signup.
// The submit click still fires as a custom "registration_submit" event.
const META_STANDARD = {
  page_view: "PageView",
  registration_success: "CompleteRegistration",
};

let initialised = false;

function loadMetaPixel(id) {
  if (window.fbq) return;
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", id);
}

function loadGA4(id) {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", id);
}

function loadGTM(id) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  document.head.appendChild(s);
}

export function initAnalytics() {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  if (META_PIXEL_ID) loadMetaPixel(META_PIXEL_ID);
  if (GA4_ID) loadGA4(GA4_ID);
  if (GTM_ID) loadGTM(GTM_ID);
}

// Read a first-party cookie (used for Meta _fbp/_fbc matching signals).
export function getCookie(name) {
  if (typeof document === "undefined") return undefined;
  const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return hit ? hit.slice(name.length + 1) : undefined;
}

// Generate a unique id shared by the browser Pixel event and the server-side
// Conversions API event so Meta can deduplicate them.
export function newMetaEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// Signals the backend forwards to Meta's Conversions API for better matching.
export function metaMatchSignals() {
  return {
    fbp: getCookie("_fbp"),
    fbc: getCookie("_fbc"),
    page_url: typeof window !== "undefined" ? window.location.href : undefined,
  };
}

export function track(event, properties = {}, options = {}) {
  if (typeof window === "undefined") return;

  // Meta Pixel. PageView is already fired by the base code in public/index.html,
  // so skip it here to avoid duplicate PageView warnings in Meta Pixel Helper.
  if (window.fbq && event !== "page_view") {
    const std = META_STANDARD[event];
    // eventID enables deduplication against the server-side CAPI event.
    const fbqOpts = options.eventId ? { eventID: options.eventId } : undefined;
    if (std) window.fbq("track", std, properties, fbqOpts);
    else window.fbq("trackCustom", event, properties, fbqOpts);
  }
  // GA4
  if (window.gtag) window.gtag("event", event, properties);
  // GTM dataLayer
  if (window.dataLayer) window.dataLayer.push({ event, ...properties });

  // Best-effort backend log (non-blocking)
  try {
    axios.post(`${API}/analytics/event`, { event, properties }).catch((e) => {
      logger.debug("[analytics] backend log failed", e);
    });
  } catch (e) {
    logger.debug("[analytics] backend log threw", e);
  }

  logger.debug("[analytics]", event, properties);
}

// Named helpers matching the required event list
export const analytics = {
  pageView: (p) => track("page_view", p),
  primaryCtaClick: (p) => track("primary_cta_click", p),
  quizStart: (p) => track("quiz_start", p),
  quizAnswer: (p) => track("quiz_answer", p),
  quizComplete: (p) => track("quiz_complete", p),
  mythReveal: (p) => track("myth_reveal", p),
  timelineAgeSelect: (p) => track("timeline_age_select", p),
  videoPlay: (p) => track("video_play", p),
  formStart: (p) => track("form_start", p),
  formStepComplete: (p) => track("form_step_complete", p),
  registrationSubmit: (p) => track("registration_submit", p),
  registrationSuccess: (p, eventId) => track("registration_success", p, { eventId }),
  registrationError: (p) => track("registration_error", p),
  calendarAdd: (p) => track("calendar_add", p),
};

export default analytics;
